import { useState, useEffect, type ReactElement } from 'react';
import { Link } from 'react-router';
import recipeService from '../lib/recipe-service';
import { recipePath } from '../lib/paths';
import MatchRow from './MatchRow';
import { type Inspiration } from '../types';

/**
 * "Cookable now" — pantry-powered suggestions for signed-in users. Paid plans
 * also see near-matches with the missing items named.
 */
function InspirationSection(): ReactElement | null {
	const [inspiration, setInspiration] = useState<Inspiration | null>(null);

	useEffect(() => {
		recipeService
			.getInspiration()
			.then(setInspiration)
			.catch((error) => console.error(error));
	}, []);

	if (!inspiration) return null;

	const { matched, surprise, nearMatchEnabled, pantrySize } = inspiration;
	const ready = matched.filter((match) => match.missingCount === 0).length;

	return (
		<>
			<section className='panel' aria-labelledby='cookable-heading'>
				<div className='pad-head'>
					<h2 id='cookable-heading' className='heading'>Cookable now</h2>
					<span className='figure'>{ready}<span className='sr-only'> ready to cook</span></span>
				</div>
				{pantrySize === 0 ? (
					<p className='empty caption'>
						Your pantry is empty. <Link to='/pantry'>Add what you have at home</Link> and the
						recipes you can cook will list here.
					</p>
				) : matched.length === 0 ? (
					<p className='empty caption'>
						Nothing matches your pantry yet. Add a few more items to <Link to='/pantry'>your pantry</Link>.
					</p>
				) : (
					<ul className='res-list'>
						{matched.map((match) => (
							<li key={match._id}><MatchRow match={match} /></li>
						))}
					</ul>
				)}
				{!nearMatchEnabled && pantrySize > 0 && (
					<p className='acts caption muted' style={{ margin: 0 }}>
						<span>
							<Link to='/pricing'>Upgrade</Link> to see the recipes you are one or two
							ingredients away from, with the shortfall named.
						</span>
					</p>
				)}
			</section>
			{surprise.length > 0 && (
				<section className='panel' aria-labelledby='surprise-heading'>
					<div className='pad-head'>
						<h2 id='surprise-heading' className='heading'>Or try something new</h2>
					</div>
					<ul className='res-list'>
						{surprise.map((recipe) => (
							<li key={recipe._id}>
								<Link className='res' to={recipePath(recipe)}>
									<span>
										<span className='res__name title'>{recipe.name}</span>
										<span className='res__desc caption'>{recipe.description}</span>
									</span>
									<span className='figure muted'>{recipe.duration} min</span>
								</Link>
							</li>
						))}
					</ul>
				</section>
			)}
		</>
	);
}

export default InspirationSection;
